import React from 'react';
import { Plane, Car, Bus, Train, Ship, BedDouble, type LucideIcon } from 'lucide-react';
import { CarTripsTabPanel, type CarTripsTabPanelProps } from './CarTripsTabPanel';
import { FlightsTabPanel, type FlightsTabPanelProps } from './FlightsTabPanel';
import { GroundTabPanel } from './GroundTabPanel';
import { useGroundTripService } from '../../hooks/useGroundTripService';
import type { GroundTrip } from '../../types/ground';
import type { TranslationKeys } from '../../translations';
import {
    type TripsTabId,
    writeLastTransportMode,
} from './TabNav';

type GroundService = ReturnType<typeof useGroundTripService>;

type ModeItem = {
    id: TripsTabId;
    label: string;
    icon: LucideIcon;
    activeCls: string;
};

type TransportTabPanelProps = {
    mode: TripsTabId;
    onModeChange: (id: TripsTabId) => void;
    t: TranslationKeys;
    language: string;
    currency: string;
    flightsPanel: FlightsTabPanelProps;
    carPanel: CarTripsTabPanelProps;
    busSvc: GroundService;
    trainSvc: GroundService;
    ferrySvc: GroundService;
    onViewTrain?: (trip: GroundTrip) => void;
    hotelsPanel: React.ReactNode;
};

export const TransportTabPanel: React.FC<TransportTabPanelProps> = ({
    mode,
    onModeChange,
    t,
    language,
    currency,
    flightsPanel,
    carPanel,
    busSvc,
    trainSvc,
    ferrySvc,
    onViewTrain,
    hotelsPanel,
}) => {
    const modes: ModeItem[] = [
        {
            id: 'flights',
            label: t.myFlights,
            icon: Plane,
            activeCls: 'from-sky-500 to-cyan-600 text-white shadow-sm',
        },
        {
            id: 'carTrips',
            label: t.carTrips,
            icon: Car,
            activeCls: 'from-amber-500 to-orange-600 text-white shadow-sm',
        },
        {
            id: 'buses',
            label: t.buses,
            icon: Bus,
            activeCls: 'from-violet-500 to-purple-600 text-white shadow-sm',
        },
        {
            id: 'trains',
            label: t.trains,
            icon: Train,
            activeCls: 'from-indigo-500 to-slate-700 text-white shadow-sm',
        },
        {
            id: 'ferries',
            label: t.ferries,
            icon: Ship,
            activeCls: 'from-cyan-500 to-teal-600 text-white shadow-sm',
        },
        {
            id: 'hotels',
            label: t.hotels,
            icon: BedDouble,
            activeCls: 'from-teal-500 to-teal-700 text-white shadow-sm',
        },
    ];

    const selectMode = (id: TripsTabId) => {
        writeLastTransportMode(id);
        onModeChange(id);
    };

    return (
        <div className="space-y-3">
            <div
                className="grid grid-cols-3 sm:grid-cols-6 gap-1 p-1 rounded-2xl border border-stone-200/80 dark:border-slate-700 bg-stone-100/80 dark:bg-slate-800/80"
                role="tablist"
                aria-label={t.trips}
            >
                {modes.map(item => {
                    const on = mode === item.id;
                    return (
                        <button
                            key={item.id}
                            type="button"
                            role="tab"
                            aria-selected={on}
                            onClick={() => selectMode(item.id)}
                            className={`flex min-h-11 flex-col items-center justify-center gap-0.5 rounded-xl px-0.5 py-1.5 text-[10px] sm:text-[11px] font-semibold transition-colors ${
                                on
                                    ? `bg-gradient-to-br ${item.activeCls}`
                                    : 'text-stone-600 dark:text-slate-300 hover:bg-white/60 dark:hover:bg-slate-700/60'
                            }`}
                        >
                            <item.icon className={`h-4 w-4 shrink-0 ${on ? 'text-white' : ''}`} />
                            <span className={`truncate max-w-full ${on ? 'text-white' : ''}`}>
                                {item.label}
                            </span>
                        </button>
                    );
                })}
            </div>

            {mode === 'flights' && <FlightsTabPanel {...flightsPanel} />}
            {mode === 'carTrips' && <CarTripsTabPanel {...carPanel} />}
            {mode === 'buses' && (
                <GroundTabPanel
                    mode="bus"
                    svc={busSvc}
                    t={t}
                    language={language}
                    currency={currency}
                />
            )}
            {mode === 'trains' && (
                <GroundTabPanel
                    mode="train"
                    svc={trainSvc}
                    t={t}
                    language={language}
                    currency={currency}
                    onView={onViewTrain}
                />
            )}
            {mode === 'ferries' && (
                <GroundTabPanel
                    mode="ferry"
                    svc={ferrySvc}
                    t={t}
                    language={language}
                    currency={currency}
                />
            )}
            {mode === 'hotels' && hotelsPanel}
        </div>
    );
};
